import styled from 'styled-components';
import Header from '../components/blog/Header';
import Footer from '../components/blog/Footer';
import { Container } from '../components/ui/Container';
import SEO from '../components/seo/SEO';
import { useRegionalSeo } from '../hooks/useRegionalSeo';

const PageWrapper = styled.div`
  min-height: 100vh;
  background-color: ${props => props.theme.colors.background};
`;

const Main = styled.main`
  ${Container} {
    padding-top: 2rem;
    padding-bottom: 2rem;
    
    @media (min-width: 768px) {
      padding-top: 3rem;
      padding-bottom: 3rem;
    }
    
    @media (min-width: 1024px) {
      padding-top: 4rem;
      padding-bottom: 4rem;
    }
  }
`;

const Content = styled.div`
  max-width: 48rem;
  margin: 0 auto;
`;

const PageTitle = styled.h1`
  font-family: ${props => props.theme.typography.fonts.display};
  font-size: 2rem;
  font-weight: ${props => props.theme.typography.fontWeights.semibold};
  margin-bottom: 1.5rem;
  
  @media (min-width: 768px) {
    font-size: 2.5rem;
    margin-bottom: 2rem;
  }
  
  @media (min-width: 1024px) {
    font-size: 3rem;
  }
  
  @media (min-width: 1440px) {
    font-size: 3.5rem;
  }
`;

const Section = styled.section`
  margin-bottom: 3rem;
  
  @media (min-width: 768px) {
    margin-bottom: 4rem;
  }
`;

const SectionTitle = styled.h2`
  font-family: ${props => props.theme.typography.fonts.display};
  font-size: 1.5rem;
  font-weight: ${props => props.theme.typography.fontWeights.semibold};
  margin-bottom: 1rem;
  
  @media (min-width: 768px) {
    font-size: 1.75rem;
    margin-bottom: 1.25rem;
  }
  
  @media (min-width: 1024px) {
    font-size: 2rem;
    margin-bottom: 1.5rem;
  }
`;

const Paragraph = styled.p`
  font-size: 1rem;
  color: ${props => props.theme.colors.foreground};
  line-height: 1.75;
  margin-bottom: 1.25rem;
  
  @media (min-width: 768px) {
    font-size: 1.0625rem;
  }
  
  @media (min-width: 1024px) {
    font-size: 1.125rem;
    margin-bottom: 1.5rem;
  }
`;

const About = () => {
  const regionalSeo = useRegionalSeo('/about');

  return (
    <PageWrapper>
      <SEO
        title="About Bloomwik"
        description="Learn about Bloomwik — a community-driven blog for technology, design, lifestyle, business, wellness, and travel."
        url="/about"
        language={regionalSeo.language}
        availableLanguages={regionalSeo.availableLanguages}
        alternateUrls={regionalSeo.alternateUrls}
      />
      <Header />
      <Main>
        <Container>
          <Content>
            <PageTitle>About Bloomwik</PageTitle>
            
            <Section>
              <SectionTitle>Our Story</SectionTitle>
              <Paragraph>
                Bloomwik was born from a simple belief: great ideas deserve great platforms. We're a community-driven blog 
                that brings together passionate writers, curious readers, and thought-provoking content across technology, 
                design, lifestyle, business, wellness, and travel.
              </Paragraph>
              <Paragraph>
                Since our launch, we've been committed to creating a space where knowledge flows freely, perspectives 
                are shared openly, and inspiration is found in every article. Our platform serves as a bridge between 
                experts and learners, storytellers and listeners, creators and appreciators.
              </Paragraph>
            </Section>

            <Section>
              <SectionTitle>Our Mission</SectionTitle>
              <Paragraph>
                To democratize knowledge and make quality content accessible to everyone. We believe that everyone has 
                a story worth telling and insights worth sharing. Our mission is to provide the tools, platform, and 
                community that empowers writers to reach their audience and readers to discover content that matters.
              </Paragraph>
            </Section>

            <Section>
              <SectionTitle>What We Value</SectionTitle>
              <Paragraph>
                <strong>Quality over Quantity:</strong> Every article on Bloomwik is carefully curated to ensure it 
                provides real value to our readers.
              </Paragraph>
              <Paragraph>
                <strong>Diversity of Thought:</strong> We celebrate different perspectives and encourage writers from 
                all backgrounds to share their unique insights.
              </Paragraph>
              <Paragraph>
                <strong>Community First:</strong> Our readers and writers are at the heart of everything we do. 
                We're building more than a blog—we're building a community.
              </Paragraph>
            </Section>

            <Section>
              <SectionTitle>Join Us</SectionTitle>
              <Paragraph>
                Whether you're a writer looking to share your expertise or a reader seeking inspiration, Bloomwik 
                welcomes you. Explore our articles, discover new perspectives, and become part of a community 
                that values knowledge, creativity, and connection.
              </Paragraph>
            </Section>
          </Content>
        </Container>
      </Main>
      <Footer />
    </PageWrapper>
  );
};

export default About;

